import React, { useState } from 'react';
import { useNotice } from '../../context/NoticeContext';
import { FaTimes } from 'react-icons/fa';
import { notify } from '../../components/CustomToast';
import { useI18n } from '../../i18n';
import './NoticeboardSection.css';

const CreateNoticeModal = ({ onClose, teamId, notice }) => {
    const { t } = useI18n();
    const { addNotice, updateNotice } = useNotice();
    const isEdit = Boolean(notice);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        team_id: teamId,
        title: notice?.title || '',
        description: notice?.description || '',
        date: notice?.date ? notice.date.split('T')[0] : new Date().toISOString().split('T')[0],
        time: notice?.time || new Date().toTimeString().split(' ')[0].substring(0, 5),
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error for this field when typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            const response = isEdit
                ? await updateNotice(notice.id, formData)
                : await addNotice(formData);
            if (response.status) {
                notify('success', response.message || t('common.status.success'));
                onClose();
            }
        } catch (error) {
            if (error.status === 422) {
                setErrors(error.errors || {});
                notify('error', t('common.validation.error'));
            } else {
                const errorMsg = error.message || t('common.errors.failed_action');
                notify('error', errorMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="notice-composer-overlay">
            <div className="notice-composer-modal">
                <div className="notice-composer-header">
                    <h3 className="notice-composer-title">{isEdit ? t('common.actions.edit') : t('legacy.add_new_notice')}</h3>
                    <button className="notice-composer-close" onClick={onClose} aria-label={t('common.actions.close')}>
                        <FaTimes />
                    </button>
                </div>

                <div className="notice-composer-body">
                    <form onSubmit={handleSubmit} className="notice-form" noValidate id="notice-form">
                        <div className="notice-form-group">
                            <label className="notice-form-label" htmlFor="title">
                                {t('legacy.notice_title_label')}<span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                className={`notice-form-input ${errors.title ? 'is-invalid' : ''}`}
                                value={formData.title}
                                onChange={handleChange}
                                placeholder={t('legacy.notice_title_label')}
                            />
                            {errors.title && <div className="field-error">{errors.title[0]}</div>}
                        </div>

                        <div className="notice-form-group">
                            <label className="notice-form-label" htmlFor="description">
                                {t('common.labels.description')}<span className="required">*</span>
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                className={`notice-form-textarea ${errors.description ? 'is-invalid' : ''}`}
                                value={formData.description}
                                onChange={handleChange}
                                placeholder={t('legacy.description_placeholder')}
                                rows="5"
                            ></textarea>
                            {errors.description && <div className="field-error">{errors.description[0]}</div>}
                        </div>

                        <div className="notice-form-row">
                            <div className="notice-form-group">
                                <label className="notice-form-label" htmlFor="date">
                                    {t('common.labels.date')}:<span className="required">*</span>
                                </label>
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    className={`notice-form-input ${errors.date ? 'is-invalid' : ''}`}
                                    value={formData.date}
                                    onChange={handleChange}
                                />
                                {errors.date && <div className="field-error">{errors.date[0]}</div>}
                            </div>

                            <div className="notice-form-group">
                                <label className="notice-form-label" htmlFor="time">
                                    {t('common.labels.time')}:<span className="required">*</span>
                                </label>
                                <input
                                    type="time"
                                    id="time"
                                    name="time"
                                    className={`notice-form-input ${errors.time ? 'is-invalid' : ''}`}
                                    value={formData.time}
                                    onChange={handleChange}
                                />
                                {errors.time && <div className="field-error">{errors.time[0]}</div>}
                            </div>
                        </div>
                    </form>
                </div>

                <div className="notice-composer-footer">
                    <button
                        type="button"
                        className="notice-composer-btn notice-composer-btn-secondary"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {t('common.actions.cancel')}
                    </button>
                    <button
                        type="submit"
                        form="notice-form"
                        className="notice-composer-btn notice-composer-btn-primary"
                        disabled={loading}
                    >
                        {loading ? t('legacy.posting') : isEdit ? t('common.actions.save') : t('common.actions.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateNoticeModal;
